/**
 * Shipping Protection — upsell-checkbox i cart drawer.
 *
 * Lägger till / tar bort en add-on-produkt (kvantitet 1) i varukorgen när
 * kunden kryssar i rutan. Följer samma mönster som gift-wrapping.js: hämtar
 * bundlade sektioner via 'cart:bundled-sections' och publicerar cartUpdate så
 * att drawern renderas om med rätt totalsumma och kryssruta.
 */
if (!customElements.get('tso-shipping-protection')) {
  customElements.define(
    'tso-shipping-protection',
    class TsoShippingProtection extends HTMLElement {
      /** Cart-attribut som minns att kunden aktivt tackat nej till skyddet. */
      static DECLINED_ATTRIBUTE = '_shipping_protection_declined';

      /**
       * Cart-attribut som minns att skyddet lades till av förvalet, inte av
       * kunden. Skiljer de två fallen åt när förvalet stängs av: automatiskt
       * tillagda rader städas bort, kundens egna val lämnas i fred.
       */
      static AUTO_ADDED_ATTRIBUTE = '_shipping_protection_auto';

      constructor() {
        super();

        this.variantId = this.getAttribute('data-variant-id');
        this.inCart = this.getAttribute('data-in-cart') === 'true';
        this.otherItemsInCart = parseInt(this.getAttribute('data-other-items'), 10) || 0;
        this.preselect = this.getAttribute('data-preselect') === 'true';
        this.declined = this.getAttribute('data-declined') === 'true';
        this.autoAdded = this.getAttribute('data-auto-added') === 'true';
      }

      get checkbox() {
        return this.querySelector('input[type="checkbox"]');
      }

      connectedCallback() {
        if (this.checkbox === null) return;

        this.checkbox.addEventListener(
          'change',
          theme.utils.debounce((event) => {
            event.target.checked ? this.addProtection() : this.removeProtection(true);
          }, 300)
        );

        // Tom varukorg: skyddet ska inte ligga kvar ensamt, och opt-out-flaggan
        // nollställs så att nästa varukorg börjar om från förvalet igen. Utan
        // det här skulle ett enda urkryss blockera förvalet för all framtid —
        // varken /cart/clear.js eller en tömd varukorg rensar cart-attribut.
        if (this.otherItemsInCart === 0) {
          if (this.inCart) return this.removeProtection();
          if (this.declined) return this.clearDeclined();
          return;
        }

        // Förvalet är avstängt men skyddet ligger kvar från när det var på —
        // städa bort det. Bara rader som förvalet självt lade till; har kunden
        // kryssat i rutan själv ska varan förstås ligga kvar.
        if (!this.preselect && this.inCart && this.autoAdded) {
          return this.removeProtection();
        }

        // Förval: lägg till skyddet när varukorgen öppnas — men aldrig igen om
        // kunden själv har kryssat ur det i den här varukorgen.
        if (this.preselect && !this.inCart && !this.declined) {
          this.addProtection(true);
        }
      }

      get bundledSections() {
        const sections = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections } })
        );
        return sections;
      }

      /**
       * @param {boolean} auto - true när förvalet lägger till skyddet, false
       *   när kunden själv kryssar i rutan.
       */
      addProtection(auto = false) {
        this.updateCart(1, {
          // Kunden väljer till skyddet igen — nollställ opt-out-flaggan.
          [TsoShippingProtection.DECLINED_ATTRIBUTE]: '',
          [TsoShippingProtection.AUTO_ADDED_ATTRIBUTE]: auto ? 'true' : '',
        });
      }

      /**
       * @param {boolean} userInitiated - true när kunden själv kryssade ur.
       *   Bara då sätts opt-out-flaggan; automatisk borttagning (tom varukorg,
       *   eller att förvalet stängts av) ska inte räknas som ett aktivt nej.
       */
      removeProtection(userInitiated = false) {
        this.updateCart(0, {
          [TsoShippingProtection.DECLINED_ATTRIBUTE]: userInitiated ? 'true' : '',
          [TsoShippingProtection.AUTO_ADDED_ATTRIBUTE]: '',
        });
      }

      /**
       * Nollställer opt-out-flaggan utan att röra varulistan. Körs tyst — ingen
       * cartUpdate publiceras, eftersom inget synligt har ändrats.
       */
      clearDeclined() {
        const body = JSON.stringify({
          attributes: { [TsoShippingProtection.DECLINED_ATTRIBUTE]: '' },
        });

        fetch(theme.routes.cart_update_url, { ...theme.utils.fetchConfig(), ...{ body } }).catch(
          (error) => console.error('Shipping protection:', error)
        );
      }

      updateCart(quantity, attributes) {
        this.enableLoading();

        const body = JSON.stringify({
          updates: { [this.variantId]: quantity },
          attributes,
          sections: this.bundledSections,
        });

        fetch(theme.routes.cart_update_url, { ...theme.utils.fetchConfig(), ...{ body } })
          .then((response) => response.json())
          .then((parsedState) => {
            if (parsedState.status) {
              this.onError(parsedState.description || parsedState.message);
              return;
            }

            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
              source: 'tso-shipping-protection',
              cart: parsedState,
            });
          })
          .catch((error) => this.onError(error));
      }

      enableLoading() {
        this.classList.add('is-loading');
      }

      onError(error) {
        console.error('Shipping protection:', error);

        this.classList.remove('is-loading');

        // Ställ tillbaka kryssrutan så den speglar varukorgen igen.
        if (this.checkbox) this.checkbox.checked = this.inCart;
      }
    }
  );
}
